import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Users, Store, DollarSign, MapPin, LogOut, CheckCircle2, XCircle } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { orderService, kioskService, beachService } from '../../services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    totalKiosks: 0,
    totalBeaches: 0,
    pendingKiosks: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [allKiosks, setAllKiosks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [orders, kiosks, beaches] = await Promise.all([
        orderService.list(),
        kioskService.list(),
        beachService.list(),
      ]);

      const todayOrders = orders.filter((o) => {
        const today = new Date().toDateString();
        return new Date(o.created_at).toDateString() === today;
      });

      const revenue = orders
        .filter((o) => ['delivered', 'preparing', 'out_for_delivery'].includes(o.status))
        .reduce((sum, o) => sum + o.total, 0);

      setStats({
        totalOrders: orders.length,
        totalRevenue: revenue,
        totalKiosks: kiosks.length,
        totalBeaches: beaches.length,
        pendingKiosks: kiosks.filter((k) => !k.is_approved).length,
      });

      setRecentOrders(orders.slice(0, 10));
      setAllKiosks(kiosks);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" text="Carregando..." />;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-accent text-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-['Outfit'] text-xl font-bold">Beach Pay Admin</h1>
            <p className="text-xs text-white/80">Painel administrativo geral</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/')}
              className="px-3 py-1.5 bg-white/20 rounded-full text-sm font-semibold hover:bg-white/30 transition-colors"
            >
              App
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); navigate('/login'); }}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl p-4 border border-border">
            <Package className="w-6 h-6 text-primary mb-2" />
            <p className="text-xs text-muted-foreground">Total Pedidos</p>
            <p className="font-bold text-2xl">{stats.totalOrders}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <DollarSign className="w-6 h-6 text-secondary mb-2" />
            <p className="text-xs text-muted-foreground">GMV</p>
            <p className="font-bold text-2xl">R$ {stats.totalRevenue.toFixed(0)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <Store className="w-6 h-6 text-accent mb-2" />
            <p className="text-xs text-muted-foreground">Estabelecimentos</p>
            <p className="font-bold text-2xl">{stats.totalKiosks}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <MapPin className="w-6 h-6 text-secondary mb-2" />
            <p className="text-xs text-muted-foreground">Praias</p>
            <p className="font-bold text-2xl">{stats.totalBeaches}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-border">
            <Users className="w-6 h-6 text-primary mb-2" />
            <p className="text-xs text-muted-foreground">Aprovações</p>
            <p className="font-bold text-2xl">{stats.pendingKiosks}</p>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-4 border-b border-border">
            <h2 className="font-['Outfit'] font-bold text-lg">Pedidos Recentes</h2>
          </div>
          <div className="divide-y divide-border">
            {recentOrders.length === 0 ? (
              <p className="p-6 text-center text-muted-foreground">Nenhum pedido ainda</p>
            ) : recentOrders.map((order) => (
              <div key={order.id} className="p-4 flex items-center justify-between" data-testid={`admin-order-${order.id}`}>
                <div>
                  <p className="font-semibold">#{order.order_number?.slice(-6)}</p>
                  <p className="text-xs text-muted-foreground">
                    {order.items.length} itens · {new Date(order.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary">R$ {order.total.toFixed(2)}</p>
                  <span className="text-xs text-muted-foreground">{order.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Kiosks */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-4 border-b border-border">
            <h2 className="font-['Outfit'] font-bold text-lg">Estabelecimentos</h2>
          </div>
          <div className="divide-y divide-border">
            {allKiosks.map((kiosk) => (
              <div key={kiosk.id} className="p-4 flex items-center justify-between" data-testid={`admin-kiosk-${kiosk.id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🏖️</span>
                  <div>
                    <p className="font-semibold">{kiosk.display_name}</p>
                    <p className="text-xs text-muted-foreground">{kiosk.cpf_cnpj}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {kiosk.is_approved ? (
                    <span className="bg-secondary/20 text-secondary px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Aprovado
                    </span>
                  ) : (
                    <span className="bg-accent/20 text-accent px-2 py-1 rounded-full text-xs font-semibold">
                      Pendente
                    </span>
                  )}
                  {kiosk.is_open && (
                    <span className="bg-primary/20 text-primary px-2 py-1 rounded-full text-xs font-semibold">
                      Aberto
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground py-4">
          Beach Pay · Admin v1.0.0
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
